<!--
  Permite elegir un icono para una aplicación o página mediante una ventana de búsqueda.
  Solo muestra una página de resultados para evitar dibujar el catálogo completo.

  El icono elegido permanece al principio de la lista, aunque no coincida con la
  búsqueda. El control lleva etiqueta para identificarlo como un campo editable.
-->
<script lang="ts">
  import { iconName } from "@shared/icons";

  import { cx } from "../lib/cx";
  import Icon from "./Icon.svelte";
  import IconPickerModal from "./IconPickerModal.svelte";
  import Field from "./ui/Field.svelte";

  let {
    value,
    onChange,
    label = "Asignar icono",
    bare = false,
    class: className,
  }: {
    value: string;
    onChange: (next: string) => void;
    /** Lo que dice la etiqueta de encima. Igual en la aplicación y en la página. */
    label?: string;
    /**
     * Solo el botón, sin etiqueta ni caja: para pegarlo a otro control dentro
     * de un `.join`, que necesita el botón como hijo directo.
     */
    bare?: boolean;
    /** Clase raíz, por si hay que alinearlo distinto. */
    class?: string;
  } = $props();

  let open = $state(false);

  /* Lo guardado puede traer el nombre del paquete de antes (`Table01Icon`): se
     traduce una vez, para que el elegido case con los de la lista. */
  const current = $derived(iconName(value));
</script>

{#snippet trigger()}
  <button
    type="button"
    onclick={() => (open = true)}
    data-tip="Cambiar el icono"
    aria-label="Icono {current}. Tocar para cambiarlo."
    class={cx("icon-picker-btn btn", bare && "icon-picker-btn-bare", bare && className)}
  >
    <Icon name={current} size={20} />
    <Icon name="chevron-down" size={12} class="icon-picker-caret" />
  </button>
{/snippet}

{#if bare}
  {@render trigger()}
{:else}
  <div class={cx("icon-picker", className)}>
    <!-- `Field` envuelve el control en su `<label>`, y un `<button>` si se
         etiqueta: tocar el texto también abre el buscador. -->
    <Field {label}>
      {@render trigger()}
    </Field>
  </div>
{/if}

<IconPickerModal
  {open}
  value={current}
  onClose={() => (open = false)}
  onPick={(name) => {
    onChange(name);
    open = false;
  }}
/>

<style>
  /* No crece: se queda con el ancho que pide su etiqueta y el nombre se lleva
     el resto de la fila. El botón se estira hasta ese ancho, así el control y
     lo que lo nombra miden lo mismo. */
  .icon-picker {
    flex: 0 0 auto;
  }

  /* El botón es `.btn` del catálogo --misma altura que el campo de al lado--
     con el icono a un lado y la punta de flecha al otro --`chevron-down`, la
     misma que dice "aquí hay lista" en el resto del panel--. Al apuntarlo se
     tiñe del acento, porque lo que abre es el icono de la aplicación y conviene
     que se lea como algo que se elige, no que se pulsa. */
  .icon-picker-btn {
    width: 100%;
    justify-content: space-between;
    padding-inline: var(--sp-10);
    color: var(--text-secondary);

    /* La punta de flecha acompaña y no compite con el icono elegido. */
    & :global(.icon-picker-caret) {
      color: var(--text-muted);
    }

    /* Suelto no se estira: lo que lo acompaña en el `.join` se lleva el resto. */
    &.icon-picker-btn-bare {
      width: auto;
      gap: var(--sp-6);
    }

    &:hover {
      border-color: color-mix(in oklab, var(--accent) 50%, transparent);
      background: var(--accent-soft);
      color: var(--accent-soft-text);

      & :global(.icon-picker-caret) {
        color: inherit;
      }
    }
  }
</style>
